<template>
  <div class="performance-table-wrapper">
    <b-table id="performance-table" striped hover :items="table.items" :fields="table.fields" show-empty>
      <template slot="empty">

        <div v-if="isLoading">
          <table-loader/>
        </div>

        <div v-else>
          Unfortunately no data available.
        </div>

      </template>
    </b-table>

    <last-updated :date="lastUpdated"/>
  </div>
</template>

<script>
  // Api
  import TickerApi from "@/services/ticker";

  // Components
  import TableLoader from '@/components/ui/loaders/TableLoader';
  import LastUpdated from '@/components/ui/table/LastUpdatedDate';

  // Mixins
  import {mixinDatesHandler} from '@/mixins/dates-handler';
  import {mixinAmountsHandler} from '@/mixins/amounts-handler';

  export default {
    name: 'SharesPerformance',

    mixins: [mixinDatesHandler, mixinAmountsHandler],

    components: {TableLoader, LastUpdated},

    data() {
      return {
        isLoading: false,

        table: {
          updateDate: null,
          fields: [
            {
              key: 'label',
              sortable: false
            },
            {
              key: 'value',
              sortable: false
            },
          ],
          items: [],
        },

        data: {},
      }
    },

    methods: {
      startLoader() {
        this.isLoading = true;
      },

      stopLoader() {
        this.isLoading = false;
      },

      fetchPerformanceData() {
        return new Promise((resolve) => {
          new TickerApi({}, {urlExtension: 'Performance'}).get(function (response) {
            if (response.status !== 200) {
              return resolve([]);
            }

            return resolve(response.data);
          });
        });
      },

      setPerformanceInstrumentsData(performanceData) {
        return new Promise((resolve) => {
          const performance = performanceData.Instruments[0];
          let items         = [];

          items.push({
            label: 'Symbol',
            value: performance.Symbol,
          });

          items.push({
            label: 'Currency',
            value: performance.TradeCurrency,
          });

          items.push({
            label: 'Market place',
            value: performance.MarketPlace,
          });

          items.push({
            label: 'Closing price',
            value: this.formatAmount(performance.ClosePrice1D, 2),
          });

          items.push({
            label: `Latest price (${this.formatDate(performance.LastUpdate)})`,
            value: this.formatAmount(performance.LastPrice, 2),
          });

          items.push({
            label: `Year high (${this.formatDate(performance.HighPrice1YDate)})`,
            value: this.formatAmount(performance.HighPrice1Y, 2),
          });

          items.push({
            label: `Year Low (${this.formatDate(performance.LowPrice1YDate)})`,
            value: this.formatAmount(performance.LowPrice1Y, 2),
          });

          items.push({
            label: `All time high (${this.formatDate(performance.AthDate)})`,
            value: this.formatAmount(performance.Ath, 2),
          });

          items.push({
            label: `All time low (${this.formatDate(performance.AtlDate)})`,
            value: this.formatAmount(performance.Atl, 2),
          });

          items.push({
            label: 'Number of shares',
            value: this.formatAmount(performance.NumberOfShares),
          });

          resolve(items);
        });
      },

      formatDate(date) {
        return this._mixinDateHandler_ApiResponseDateTimeToFormat(date, 'DD MMM YYYY', true);
      },

      formatAmount(amount, decimals) {
        return this._mixinAmountsHandler_formatAmount(amount, decimals);
      },

      async fetchData() {
        this.startLoader();

        let data = await this.fetchPerformanceData();

        this.data = data;

        if ('Instruments' in data && data.Instruments.length > 0) {
          this.table.items = await this.setPerformanceInstrumentsData(data);
        }

        this.stopLoader();
      }
    },

    computed: {
      lastUpdated() {
        const data = this.data;

        if (data.Instruments && data.Instruments.length > 0) {
          return data.Instruments[0].LastUpdate;
        }

        return null;
      }
    },

    mounted: function () {
      this.$nextTick(function () {
        this.fetchData();
      });
    },
  }
</script>

<style scoped lang="scss">
  .performance-table-wrapper {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
  }

  ::v-deep #performance-table {
    thead {
      display: none;
    }

    tr:not(.b-table-empty-row) td:nth-child(1) {
      font-weight: bold;
    }

    tr td:nth-child(2) {
      text-align: right;
    }

    .b-table-empty-row {
      text-align: center;
    }
  }
</style>
